import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Button, Progress, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/errors';
import { getProjectWorkload } from '../api/projects';
import type { ProjectWorkloadItem } from '../api/projects';
import { listUserAccounts } from '../api/userAccounts';
import type { UserAccount } from '../api/userAccounts';

/** Số bản ghi nạp cho danh sách người dùng. */
const lookupPageSize = 200;

/** Ngưỡng phần trăm coi là quá tải. Khớp với ngưỡng của máy chủ. */
const overloadThreshold = 100;

/**
 * Một dòng của bảng, đã ghép tên người vào con số phân bổ.
 *
 * Máy chủ chỉ trả về định danh người dùng, cố ý: điểm cuối phân bổ nguồn lực không áp phạm vi dữ
 * liệu theo dòng, nên nó không được trả về thứ gì lộ ra dự án nào của ai. Tên người thì lấy từ danh
 * mục tài khoản, vốn có quyền xem riêng.
 */
interface WorkloadRow extends ProjectWorkloadItem {
  readonly displayName: string;
}

/**
 * Bảng phân bổ nguồn lực.
 *
 * Trả lời đúng một câu hỏi: <b>ai đang quá tải</b>. Đó là câu hỏi hay gặp nhất ở công ty nhỏ, nơi
 * một kỹ sư thường chạy ba bốn dự án cùng lúc và không ai có bức tranh tổng.
 *
 * <b>Chỉ cộng những dòng còn tham gia và những dự án chưa đóng.</b> Một người còn tên trong năm dự
 * án đã đóng không hề bận vì chúng, và tính vào sẽ làm mọi người trông như đang quá tải — khi đó
 * bảng này mất hết tác dụng.
 *
 * <b>Người chưa được phân bổ vào dự án nào thì không xuất hiện.</b> Đó là hành vi đúng của một bảng
 * trả lời câu hỏi <i>ai đang quá tải</i>, nhưng nó cũng có nghĩa bảng này <b>không</b> trả lời được
 * câu hỏi ngược lại là <i>ai đang rảnh</i>. Ghi rõ ngay trên màn hình để người dùng không suy ra
 * nhầm.
 */
export function ProjectWorkloadPage(): ReactNode {
  const [rows, setRows] = useState<readonly WorkloadRow[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        const workload = await getProjectWorkload();

        /*
          Tên người là thứ CÓ CŨNG ĐƯỢC, và phải được lấy theo cách chịu được thất bại.

          Danh mục tài khoản đòi quyền xem tài khoản người dùng — một quyền KHÁC với quyền xem thành
          viên dự án. Một người quản lý dự án có quyền xem bảng này nhưng không có quyền xem danh mục
          tài khoản là chuyện hoàn toàn bình thường, và nếu gộp hai lời gọi vào một phép chờ chung thì
          cả trang sẽ báo lỗi chỉ vì thiếu phần tên — trong khi phần quan trọng là các con số đã lấy
          được rồi.
        */
        let users: readonly UserAccount[] = [];

        try {
          const page = await listUserAccounts({ pageSize: lookupPageSize });

          users = page.items;
        } catch {
          // Bỏ qua có chủ ý: bảng vẫn dùng được với định danh thay cho tên.
          users = [];
        }

        // Chỉ kiểm MỘT lần, sau cả hai lời gọi. Kiểm thêm một lần ở giữa là thừa — không có gì
        // giữa hai lời gọi chạm vào trạng thái của thành phần — và bộ kiểm tĩnh bắt đúng điều đó.
        if (!isCurrent) {
          return;
        }

        setRows(workload.map((item) => toRow(item, users)));
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được bảng phân bổ nguồn lực.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const columns: TableColumnsType<WorkloadRow> = [
    {
      title: 'Người',
      dataIndex: 'displayName',
      render: (displayName: string, row) => (
        <Space size={4}>
          <span>{displayName}</span>
          {row.isOverloaded ? (
            <Tooltip title="Tổng phần trăm thời gian trên các dự án đang chạy đã vượt 100%.">
              <Tag color="red" icon={<WarningOutlined />}>
                Quá tải
              </Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Số dự án đang tham gia',
      dataIndex: 'activeProjectCount',
      width: 190,
      align: 'right',
      sorter: (left, right) => left.activeProjectCount - right.activeProjectCount,
    },
    {
      title: 'Tổng thời gian đã phân bổ',
      dataIndex: 'totalAllocationPercent',
      width: 280,
      defaultSortOrder: 'descend',
      sorter: (left, right) => left.totalAllocationPercent - right.totalAllocationPercent,
      render: (percent: number, row) => (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <Progress
            percent={Math.min(Math.round(percent), overloadThreshold)}
            size="small"
            status={row.isOverloaded ? 'exception' : 'normal'}
            format={() => `${Math.round(percent).toString()}%`}
          />
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Phân bổ nguồn lực
        </Typography.Title>
        <Button icon={<ReloadOutlined />} onClick={reload}>
          Tải lại
        </Button>
      </Space>

      {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

      <Alert
        type="info"
        showIcon
        message="Bảng này trả lời câu hỏi ai đang quá tải, không phải ai đang rảnh"
        description="Chỉ những người đã được phân bổ vào ít nhất một dự án chưa đóng mới xuất hiện ở đây. Người chưa nhận dự án nào, hoặc đã tham gia nhưng chưa khai phần trăm thời gian, sẽ không có dòng — đó không phải lỗi."
      />

      {rows !== null && rows.length > 0 && rows.every((row) => row.displayName === row.userAccountId) ? (
        <Alert
          type="warning"
          showIcon
          message="Đang hiện định danh thay cho tên người"
          description="Tài khoản của bạn không có quyền xem danh mục tài khoản người dùng, nên hệ thống không tra được tên. Các con số phân bổ vẫn đúng."
        />
      ) : null}

      <Table<WorkloadRow>
        rowKey="userAccountId"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={rows as WorkloadRow[] | undefined}
        loading={rows === null && errorMessage === null}
      />
    </Space>
  );
}

/**
 * Ghép tên người vào một dòng phân bổ.
 *
 * Khi không tra được tên thì hiển thị chính định danh thay vì để trống: một dòng không tên vẫn cho
 * biết có người đang quá tải, còn một dòng trống thì không nói gì cả.
 */
function toRow(item: ProjectWorkloadItem, users: readonly UserAccount[]): WorkloadRow {
  const user = users.find((candidate) => candidate.id === item.userAccountId);

  return {
    ...item,
    displayName: user === undefined ? item.userAccountId : `${user.fullName} (${user.userName})`,
  };
}
